import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Filter, Sparkles, Clock, ChevronRight } from "lucide-react";
import TopNav from "../components/TopNav";
import api from "../lib/api";

const TRACKS = ["all", "operator", "engineer", "hunter", "leader"];
const DIFFICULTIES = ["all", "junior", "intermediate", "senior", "expert"];

const DIFF_TAG = {
  junior: "tag-green",
  intermediate: "tag-cyan",
  senior: "tag-amber",
  expert: "tag-red",
};

export default function Scenarios() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [track, setTrack] = useState("all");
  const [difficulty, setDifficulty] = useState("all");

  useEffect(() => {
    setLoading(true);
    api.get("/scenarios/library")
      .then((r) => setRows(r.data || []))
      .catch((e) => setErr(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => rows.filter((s) => {
    if (track !== "all" && s.track !== track) return false;
    if (difficulty !== "all" && s.difficulty !== difficulty) return false;
    return true;
  }), [rows, track, difficulty]);

  const counts = useMemo(() => {
    const c = { all: rows.length };
    for (const t of TRACKS.slice(1)) c[t] = rows.filter((s) => s.track === t).length;
    return c;
  }, [rows]);

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <TopNav />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
          <div className="flex items-center gap-3">
            <Sparkles size={20} color="var(--cyan)" strokeWidth={1.5} />
            <div className="overline">// SENTINEL_SCENARIO_LIBRARY</div>
          </div>
          <Link to="/practical" className="btn-ghost inline-flex items-center gap-2 text-xs" data-testid="scen-go-practical">
            <BookOpen size={14} /> Open Range
          </Link>
        </div>
        <h1 className="font-display font-bold text-4xl uppercase mb-2" data-testid="scenarios-title">Scenario Library</h1>
        <p className="text-sm text-[var(--text-2)] max-w-2xl mb-8">
          Every scenario here was minted by Sentinel from real defensive-cyber telemetry patterns and is mapped to MITRE ATT&amp;CK. Filter by your track and click in to drill.
        </p>

        {/* Track tabs */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="scen-track-tabs">
          {TRACKS.map((t) => (
            <button
              key={t}
              onClick={() => setTrack(t)}
              className={`px-3 py-1.5 font-mono text-[10px] tracking-widest uppercase border ${
                track === t
                  ? "border-[var(--cyan)] text-[var(--cyan)] bg-[var(--surface)]"
                  : "border-[var(--border)] text-[var(--text-2)] hover:text-white"
              }`}
              data-testid={`scen-tab-${t}`}
            >
              {t} <span className="ml-1 text-[var(--text-2)]">{counts[t] ?? 0}</span>
            </button>
          ))}
        </div>

        {/* Difficulty filter */}
        <div className="flex items-center gap-2 mb-8 flex-wrap" data-testid="scen-diff-filter">
          <Filter size={12} className="text-[var(--text-2)]" />
          <span className="font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)]">DIFFICULTY:</span>
          {DIFFICULTIES.map((d) => (
            <button
              key={d}
              onClick={() => setDifficulty(d)}
              className={`px-2 py-1 font-mono text-[10px] tracking-widest uppercase border ${
                difficulty === d
                  ? "border-[var(--amber)] text-[var(--amber)]"
                  : "border-[var(--border)] text-[var(--text-2)] hover:text-white"
              }`}
              data-testid={`scen-diff-${d}`}
            >
              {d}
            </button>
          ))}
        </div>

        {loading && <div className="font-mono text-xs text-[var(--text-2)]">// loading scenarios…</div>}
        {err && <div className="font-mono text-xs text-[var(--red)] border border-[var(--red)] p-3" data-testid="scen-error">{err}</div>}

        {!loading && !err && filtered.length === 0 && (
          <div className="flat-card p-8 text-center" data-testid="scen-empty">
            <div className="font-display uppercase text-lg">No scenarios match</div>
            <p className="text-sm text-[var(--text-2)] mt-2">
              Leaders can mint fresh ones — open the Practical Range and use the <span className="text-[var(--cyan)]">Generate</span> tab.
            </p>
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="scen-grid">
          {filtered.map((s) => (
            <Link
              key={s.id || s.slug}
              to={`/practical/${s.slug}`}
              className="flat-card p-5 hover:border-[var(--cyan)] transition-colors group"
              data-testid={`scen-card-${s.slug}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="font-mono text-[10px] tracking-widest uppercase text-[var(--cyan)]">
                  TRK-{s.track} · {s.exercise_type}
                </div>
                <span className={`tag ${DIFF_TAG[s.difficulty] || "tag-cyan"}`}>{s.difficulty}</span>
              </div>
              <div className="font-display font-bold uppercase text-lg leading-tight group-hover:text-[var(--cyan)]">
                {s.title}
              </div>
              <p className="text-sm text-[var(--text-2)] mt-2 line-clamp-3">{s.summary}</p>
              <div className="flex items-center justify-between mt-4 font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)]">
                <span className="inline-flex items-center gap-1"><Clock size={10} /> {s.estimated_minutes}m</span>
                <span className="inline-flex items-center gap-1 text-[var(--cyan)] opacity-0 group-hover:opacity-100 transition-opacity">
                  Drill <ChevronRight size={12} />
                </span>
              </div>
              {s.mitre_techniques?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {s.mitre_techniques.slice(0, 4).map((t) => (
                    <span key={t} className="font-mono text-[9px] tracking-widest text-[var(--text-2)] border border-[var(--border)] px-1.5 py-0.5">
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
}
